import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiWrite, list, type Role } from '../lib/api'
import { useAuth } from '../auth'
import { Empty, PageHead, Skel } from '../components/ui'
import { thaiDate, thaiDateTime } from '../lib/format'

/**
 * Key-vault access policies and the append-only access log (part 64).
 *
 * The page never displays key material: it only names key purposes such as field_crypto. An
 * evaluation is a real recorded decision, not a simulation, so every press of "ตรวจสอบสิทธิ์"
 * appends a row that the log below reads back from the server.
 */

type AccessPurpose = 'decrypt' | 'sign' | 'rotate' | 'read_metadata'

interface VaultPolicy {
  id: string
  name: string
  description: string
  roles: string[]
  purposes: string[]
  key_scopes: string[]
  source_cidrs: string[]
  days_of_week: number[]
  window_start_minute: number
  window_end_minute: number
  active_from?: string
  active_until?: string
  priority: number
  enabled: boolean
  created_at: string
  updated_at: string
}

interface VaultDecision {
  allowed: boolean
  reason_code: string
  reason: string
  policy_id?: string
  policy_name?: string
  log_id?: string
  role: string
  purpose: string
  key_scope: string
  source_ip: string
  evaluated_at: string
}

interface VaultAccessRow {
  id: string
  occurred_at: string
  actor_id?: string
  actor_role: string
  access_purpose: string
  key_scope: string
  source_ip: string
  allowed: boolean
  reason_code: string
  reason: string
  policy_id?: string
  policy_name: string
  request_id: string
}

const roleOptions: Array<{ value: Role; label: string }> = [
  { value: 'owner', label: 'เจ้าของ' },
  { value: 'admin', label: 'ผู้ดูแลระบบ' },
  { value: 'audit_manager', label: 'ผู้จัดการงานตรวจสอบ' },
  { value: 'auditor', label: 'ผู้ตรวจสอบ' },
  { value: 'reviewer', label: 'ผู้ทบทวน' },
  { value: 'client_viewer', label: 'ผู้ดูฝั่งลูกค้า' },
]

const purposeOptions: Array<{ value: AccessPurpose; label: string }> = [
  { value: 'decrypt', label: 'ถอดรหัส' },
  { value: 'sign', label: 'ลงลายมือชื่อ' },
  { value: 'rotate', label: 'หมุนเวียนคีย์' },
  { value: 'read_metadata', label: 'อ่านข้อมูลเมตาของคีย์' },
]

const scopeOptions: Array<{ value: string; label: string }> = [
  { value: 'mfa', label: 'MFA' },
  { value: 'field_crypto', label: 'เข้ารหัสฟิลด์' },
  { value: 'api_key_pepper', label: 'API key pepper' },
  { value: 'report_signing', label: 'ลงนามรายงาน' },
]

const dayLabels = ['อา.', 'จ.', 'อ.', 'พ.', 'พฤ.', 'ศ.', 'ส.']

const reasonLabels: Record<string, string> = {
  allowed: 'อนุญาตตามนโยบาย',
  invalid_request: 'คำขอไม่ถูกต้อง',
  no_policy: 'ไม่มีนโยบายที่เปิดใช้',
  policy_not_yet_active: 'นโยบายยังไม่เริ่มใช้',
  policy_expired: 'นโยบายหมดอายุ',
  role_mismatch: 'บทบาทไม่ตรง',
  purpose_mismatch: 'วัตถุประสงค์ไม่ตรง',
  key_scope_mismatch: 'ขอบเขตคีย์ไม่ตรง',
  day_mismatch: 'วันไม่อยู่ในนโยบาย',
  outside_window: 'นอกช่วงเวลา',
  ip_mismatch: 'IP ไม่อยู่ในขอบเขต',
}

const POLICIES_KEY = ['vault-access-policies']
const LOG_KEY = ['vault-access-log']

function toggle<T>(values: T[], value: T): T[] {
  return values.includes(value) ? values.filter((v) => v !== value) : [...values, value]
}

function toMinutes(value: string): number {
  const [h, m] = value.split(':').map(Number)
  return (h || 0) * 60 + (m || 0)
}

function fromMinutes(value: number): string {
  if (value >= 1440) return '00:00'
  const h = Math.floor(value / 60)
  const m = value % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

function windowText(start: number, end: number): string {
  if (start === end) return 'ตลอดวัน (UTC)'
  const fmt = (m: number) => (m === 1440 ? '24:00' : fromMinutes(m))
  return `${fmt(start)}-${fmt(end)} UTC`
}

function dayListText(days: number[]): string {
  if (days.length === 0) return 'ทุกวัน'
  const names = ['อาทิตย์', 'จันทร์', 'อังคาร', 'พุธ', 'พฤหัสบดี', 'ศุกร์', 'เสาร์']
  return days.map((d) => names[d] ?? String(d)).join(', ')
}

function isoDate(value: string, endOfDay: boolean): string | null {
  if (!value) return null
  return new Date(`${value}T${endOfDay ? '23:59:59' : '00:00:00'}Z`).toISOString()
}

export function VaultAccess() {
  const { me } = useAuth()
  const qc = useQueryClient()
  const myRole: Role = me?.role ?? 'auditor'
  const canView = myRole === 'owner' || myRole === 'admin' || myRole === 'audit_manager' || myRole === 'auditor'
  const canConfigure = myRole === 'owner' || myRole === 'admin'
  const canSimulate = myRole === 'owner' || myRole === 'admin'

  const [notice, setNotice] = useState('')

  // Policy form
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [selectedRoles, setSelectedRoles] = useState<Role[]>(['auditor'])
  const [selectedPurposes, setSelectedPurposes] = useState<AccessPurpose[]>(['read_metadata'])
  const [selectedScopes, setSelectedScopes] = useState<string[]>(['field_crypto'])
  const [allScopes, setAllScopes] = useState(false)
  const [cidrs, setCidrs] = useState('')
  const [days, setDays] = useState<number[]>([])
  const [windowStart, setWindowStart] = useState('00:00')
  const [windowEnd, setWindowEnd] = useState('00:00')
  const [activeFrom, setActiveFrom] = useState('')
  const [activeUntil, setActiveUntil] = useState('')
  const [priority, setPriority] = useState('100')
  const [enabled, setEnabled] = useState(true)

  // Evaluation form
  const [checkRole, setCheckRole] = useState<Role>(myRole)
  const [checkPurpose, setCheckPurpose] = useState<AccessPurpose>('read_metadata')
  const [checkScope, setCheckScope] = useState('field_crypto')
  const [checkIP, setCheckIP] = useState('')
  const [decision, setDecision] = useState<VaultDecision | null>(null)

  const [logFilter, setLogFilter] = useState<'all' | 'allowed' | 'denied'>('all')

  const policies = useQuery({
    queryKey: POLICIES_KEY,
    queryFn: () => list<VaultPolicy>('/v1/vault/access/policies'),
    enabled: canView,
    staleTime: 15_000,
  })

  const log = useQuery({
    queryKey: [...LOG_KEY, logFilter],
    queryFn: () => list<VaultAccessRow>(
      `/v1/vault/access/log?limit=100${logFilter === 'all' ? '' : `&allowed=${logFilter === 'allowed'}`}`,
    ),
    enabled: canView,
    staleTime: 10_000,
  })

  const create = useMutation({
    mutationFn: () => apiWrite<VaultPolicy>('/v1/vault/access/policies', 'POST', {
      name: name.trim(),
      description: description.trim(),
      roles: selectedRoles,
      purposes: selectedPurposes,
      key_scopes: allScopes ? ['*'] : selectedScopes,
      source_cidrs: cidrs.split(/[\n,]/).map((c) => c.trim()).filter(Boolean),
      days_of_week: days,
      window_start_minute: toMinutes(windowStart),
      window_end_minute: toMinutes(windowEnd),
      active_from: isoDate(activeFrom, false),
      active_until: isoDate(activeUntil, true),
      priority: Number.parseInt(priority, 10) || 0,
      enabled,
    }),
    onSuccess: (row) => {
      setNotice(`สร้างนโยบาย "${row.name}" แล้ว`)
      setName('')
      setDescription('')
      setCidrs('')
      setDays([])
      setActiveFrom('')
      setActiveUntil('')
      void qc.invalidateQueries({ queryKey: POLICIES_KEY })
    },
    onError: () => setNotice(''),
  })

  const evaluate = useMutation({
    mutationFn: () => apiWrite<VaultDecision>('/v1/vault/access/evaluate', 'POST', {
      role: checkRole,
      purpose: checkPurpose,
      key_scope: checkScope,
      source_ip: checkIP.trim(),
    }),
    onMutate: () => setDecision(null),
    onSuccess: (result) => {
      setDecision(result)
      void qc.invalidateQueries({ queryKey: LOG_KEY })
    },
    onError: () => setDecision(null),
  })

  const rows = policies.data ?? []
  const logRows = log.data ?? []
  const enabledCount = rows.filter((row) => row.enabled).length
  const deniedCount = logRows.filter((row) => !row.allowed).length
  const canSubmit = canConfigure
    && name.trim().length > 0
    && selectedRoles.length > 0
    && selectedPurposes.length > 0
    && (allScopes || selectedScopes.length > 0)
    && cidrs.trim().length > 0
    && !create.isPending

  return (
    <>
      <PageHead
        title="การเข้าถึงคีย์และการตรวจสอบย้อนหลัง"
        sub="กำหนดว่าใครใช้คีย์ใด เพื่ออะไร จากเครือข่ายไหน และในช่วงเวลาใด · ค่าเริ่มต้นคือปฏิเสธเมื่อไม่มีนโยบายที่ตรง"
      />

      <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        <span className="chip">{rows.length} นโยบายทั้งหมด</span>
        <span className="chip">{enabledCount} นโยบายที่เปิดใช้</span>
        <span className="chip">{logRows.length} รายการในบันทึก</span>
        <span className="chip">{deniedCount} รายการถูกปฏิเสธ</span>
        <span className="chip">บันทึกการเข้าถึงแก้ไขไม่ได้</span>
      </div>

      <div className="grid-main">
        <div className="stack">
          <div className="card card-pad">
            <div className="row" style={{ justifyContent: 'space-between', marginBottom: 12 }}>
              <div className="card-label" style={{ marginBottom: 0 }}>นโยบายการเข้าถึงคีย์</div>
              <span className="muted" style={{ fontSize: 12 }}>เรียงตามลำดับความสำคัญ</span>
            </div>

            {policies.isLoading ? (
              <div className="stack"><Skel h={48} /><Skel h={48} /><Skel h={48} /></div>
            ) : rows.length === 0 ? (
              <Empty
                title="ยังไม่มีนโยบาย"
                body="เมื่อไม่มีนโยบายที่เปิดใช้ ระบบจะปฏิเสธทุกคำขอเข้าถึงคีย์ สร้างนโยบายแรกเพื่ออนุญาตเฉพาะบทบาทและเครือข่ายที่ต้องการ"
              />
            ) : (
              <div className="stack" style={{ gap: 12 }}>
                {rows.map((row) => (
                  <div key={row.id} style={{ border: '1px solid var(--color-hairline)', borderRadius: 14, padding: 14 }}>
                    <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                      <div>
                        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                          <strong>{row.name}</strong>
                          <span className="chip">{row.enabled ? 'เปิดใช้' : 'ปิดใช้'}</span>
                          <span className="chip">ลำดับ {row.priority}</span>
                        </div>
                        {row.description ? (
                          <div className="muted" style={{ marginTop: 6, fontSize: 12.5 }}>{row.description}</div>
                        ) : null}
                      </div>
                      <span className="mono muted" title={row.id}>{row.id.slice(0, 8)}</span>
                    </div>

                    <div className="row" style={{ gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
                      {row.roles.map((role) => (
                        <span className="chip" key={`r-${role}`}>
                          {roleOptions.find((o) => o.value === role)?.label ?? role}
                        </span>
                      ))}
                      {row.purposes.map((purpose) => (
                        <span className="chip" key={`p-${purpose}`}>
                          {purposeOptions.find((o) => o.value === purpose)?.label ?? purpose}
                        </span>
                      ))}
                      {row.key_scopes.map((scope) => (
                        <span className="chip" key={`s-${scope}`}>
                          {scope === '*' ? 'ทุกคีย์' : scopeOptions.find((o) => o.value === scope)?.label ?? scope}
                        </span>
                      ))}
                    </div>

                    <div className="mono muted" style={{ marginTop: 10, fontSize: 12 }}>
                      {row.source_cidrs.join(', ')}
                    </div>
                    <div className="row muted" style={{ gap: 12, flexWrap: 'wrap', marginTop: 6, fontSize: 12 }}>
                      <span>{dayListText(row.days_of_week)}</span>
                      <span>{windowText(row.window_start_minute, row.window_end_minute)}</span>
                      {row.active_from ? <span>เริ่ม {thaiDate(row.active_from)}</span> : null}
                      {row.active_until ? <span>ถึง {thaiDate(row.active_until)}</span> : null}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="card card-pad">
            <div className="row" style={{ justifyContent: 'space-between', marginBottom: 12 }}>
              <div className="card-label" style={{ marginBottom: 0 }}>บันทึกการเข้าถึงคีย์</div>
              <select aria-label="กรองตามผลการตัดสิน"
                className="select"
                value={logFilter}
                onChange={(e) => setLogFilter(e.target.value as 'all' | 'allowed' | 'denied')}
                style={{ width: 150 }}
              >
                <option value="all">ทุกผลลัพธ์</option>
                <option value="allowed">อนุญาต</option>
                <option value="denied">ปฏิเสธ</option>
              </select>
            </div>

            {log.isLoading ? (
              <div className="stack"><Skel h={40} /><Skel h={40} /><Skel h={40} /></div>
            ) : logRows.length === 0 ? (
              <Empty
                title="ยังไม่มีบันทึก"
                body="ทุกครั้งที่ประเมินสิทธิ์ ระบบจะบันทึกผลพร้อมเหตุผลไว้ที่นี่ ทั้งกรณีอนุญาตและปฏิเสธ"
              />
            ) : (
              <div className="table-wrap" style={{ border: 0 }}>
                <table className="table">
                  <thead>
                    <tr>
                      <th>เวลา</th>
                      <th>บทบาท</th>
                      <th>คำขอ</th>
                      <th>IP</th>
                      <th>ผล</th>
                      <th>เหตุผล</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logRows.map((row) => (
                      <tr key={row.id}>
                        <td className="mono" style={{ fontSize: 11.5, whiteSpace: 'nowrap' }}>{thaiDateTime(row.occurred_at)}</td>
                        <td style={{ fontSize: 12 }}>
                          {roleOptions.find((o) => o.value === row.actor_role)?.label ?? row.actor_role}
                        </td>
                        <td style={{ fontSize: 12 }}>
                          <div>{purposeOptions.find((o) => o.value === row.access_purpose)?.label ?? row.access_purpose}</div>
                          <div className="muted mono" style={{ fontSize: 11 }}>{row.key_scope}</div>
                        </td>
                        <td className="mono" style={{ fontSize: 11.5 }}>{row.source_ip}</td>
                        <td>
                          <span className="chip" style={{
                            color: row.allowed ? 'var(--color-emerald-soft)' : 'var(--color-amber)',
                            borderColor: row.allowed ? 'rgba(46,125,100,0.32)' : 'rgba(199,123,43,0.32)',
                          }}>
                            {row.allowed ? 'อนุญาต' : 'ปฏิเสธ'}
                          </span>
                        </td>
                        <td style={{ fontSize: 12, maxWidth: 360 }}>
                          <div>{row.reason}</div>
                          <div className="muted" style={{ fontSize: 11, marginTop: 3 }}>
                            {reasonLabels[row.reason_code] ?? row.reason_code}
                            {row.policy_name ? ` · ${row.policy_name}` : ''}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <div className="stack">
          <div className="card card-pad">
            <div className="card-label">ตรวจสอบสิทธิ์ก่อนใช้งาน</div>
            <div className="muted" style={{ fontSize: 12, lineHeight: 1.7 }}>
              การตรวจสอบนี้เป็นการตัดสินใจจริงและจะถูกบันทึกในบันทึกการเข้าถึง
            </div>

            <div className="field">
              <label htmlFor="check-role">บทบาทที่ต้องการประเมิน</label>
              <select
                id="check-role"
                className="select"
                value={checkRole}
                onChange={(e) => setCheckRole(e.target.value as Role)}
                disabled={!canSimulate}
                style={{ width: '100%' }}
              >
                {roleOptions.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>

            <div className="field">
              <label htmlFor="check-purpose">วัตถุประสงค์</label>
              <select
                id="check-purpose"
                className="select"
                value={checkPurpose}
                onChange={(e) => setCheckPurpose(e.target.value as AccessPurpose)}
                style={{ width: '100%' }}
              >
                {purposeOptions.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>

            <div className="field">
              <label htmlFor="check-scope">ขอบเขตคีย์</label>
              <select
                id="check-scope"
                className="select"
                value={checkScope}
                onChange={(e) => setCheckScope(e.target.value)}
                style={{ width: '100%' }}
              >
                {scopeOptions.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>

            <div className="field">
              <label htmlFor="check-ip">IP ต้นทาง</label>
              <input
                id="check-ip"
                className="input mono"
                placeholder="ว่างไว้เพื่อใช้ IP ของคำขอนี้"
                value={checkIP}
                onChange={(e) => setCheckIP(e.target.value)}
              />
            </div>

            <div className="row" style={{ marginTop: 18 }}>
              <button
                className="btn btn-primary"
                onClick={() => evaluate.mutate()}
                disabled={evaluate.isPending}
              >
                {evaluate.isPending ? 'กำลังตรวจสอบ…' : 'ตรวจสอบสิทธิ์'}
              </button>
            </div>

            {evaluate.error ? (
              <div className="muted" style={{ marginTop: 12, fontSize: 12.5, color: 'var(--color-amber)' }}>
                {evaluate.error instanceof Error ? evaluate.error.message : 'ตรวจสอบสิทธิ์ไม่สำเร็จ'}
              </div>
            ) : null}

            {decision ? (
              <div style={{
                marginTop: 16,
                padding: 14,
                borderRadius: 12,
                border: `1px solid ${decision.allowed ? 'rgba(46,125,100,0.35)' : 'rgba(199,123,43,0.35)'}`,
                background: decision.allowed ? 'rgba(46,125,100,0.06)' : 'rgba(199,123,43,0.06)',
              }}>
                <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                  <span className="chip" style={{
                    color: decision.allowed ? 'var(--color-emerald-soft)' : 'var(--color-amber)',
                    borderColor: decision.allowed ? 'rgba(46,125,100,0.32)' : 'rgba(199,123,43,0.32)',
                  }}>
                    {decision.allowed ? 'อนุญาต' : 'ปฏิเสธ'}
                  </span>
                  <span className="mono muted" style={{ fontSize: 11 }}>{decision.reason_code}</span>
                </div>
                <div style={{ marginTop: 10, fontSize: 13, lineHeight: 1.7 }}>{decision.reason}</div>
                <div className="muted" style={{ marginTop: 8, fontSize: 11.5 }}>
                  {decision.policy_name ? `นโยบาย: ${decision.policy_name}` : 'ไม่มีนโยบายที่อนุญาต'}
                  {decision.log_id ? ` · บันทึก ${decision.log_id.slice(0, 8)}` : ''}
                </div>
              </div>
            ) : null}
          </div>

          {canConfigure ? (
            <div className="card card-pad">
              <div className="card-label">สร้างนโยบายใหม่</div>

              {notice ? (
                <div className="muted" style={{ marginBottom: 12, fontSize: 12.5, color: 'var(--color-emerald-soft)' }}>
                  {notice}
                </div>
              ) : null}

              <div className="field">
                <label htmlFor="policy-name">ชื่อนโยบาย</label>
                <input id="policy-name" className="input" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} />
              </div>

              <div className="field">
                <label htmlFor="policy-description">คำอธิบาย</label>
                <input id="policy-description" className="input" value={description} onChange={(e) => setDescription(e.target.value)} maxLength={500} />
              </div>

              <div className="field">
                <label>บทบาทที่อนุญาต</label>
                <div className="row" style={{ flexWrap: 'wrap', gap: 6 }}>
                  {roleOptions.map((option) => (
                    <label key={option.value} className="chip" style={{ cursor: 'pointer', gap: 6 }}>
                      <input
                        type="checkbox"
                        checked={selectedRoles.includes(option.value)}
                        onChange={() => setSelectedRoles((current) => toggle(current, option.value))}
                      />
                      {option.label}
                    </label>
                  ))}
                </div>
              </div>

              <div className="field">
                <label>วัตถุประสงค์ที่อนุญาต</label>
                <div className="row" style={{ flexWrap: 'wrap', gap: 6 }}>
                  {purposeOptions.map((option) => (
                    <label key={option.value} className="chip" style={{ cursor: 'pointer', gap: 6 }}>
                      <input
                        type="checkbox"
                        checked={selectedPurposes.includes(option.value)}
                        onChange={() => setSelectedPurposes((current) => toggle(current, option.value))}
                      />
                      {option.label}
                    </label>
                  ))}
                </div>
              </div>

              <div className="field">
                <label>ขอบเขตคีย์ที่อนุญาต</label>
                <div className="row" style={{ flexWrap: 'wrap', gap: 6 }}>
                  <label className="chip" style={{ cursor: 'pointer', gap: 6 }}>
                    <input type="checkbox" checked={allScopes} onChange={() => setAllScopes((current) => !current)} />
                    ทุกคีย์
                  </label>
                  {scopeOptions.map((option) => (
                    <label key={option.value} className="chip" style={{
                      cursor: allScopes ? 'not-allowed' : 'pointer',
                      gap: 6,
                      opacity: allScopes ? 0.45 : 1,
                    }}>
                      <input
                        type="checkbox"
                        checked={selectedScopes.includes(option.value)}
                        disabled={allScopes}
                        onChange={() => setSelectedScopes((current) => toggle(current, option.value))}
                      />
                      {option.label}
                    </label>
                  ))}
                </div>
              </div>

              <div className="field">
                <label htmlFor="policy-cidrs">ช่วง IP ต้นทาง (CIDR คั่นด้วยจุลภาคหรือบรรทัดใหม่)</label>
                <textarea
                  id="policy-cidrs"
                  className="input mono"
                  rows={3}
                  placeholder="203.0.113.0/24"
                  value={cidrs}
                  onChange={(e) => setCidrs(e.target.value)}
                  style={{ height: 'auto', padding: 10 }}
                />
              </div>

              <div className="field">
                <label>วันที่อนุญาต (ไม่เลือก = ทุกวัน)</label>
                <div className="row" style={{ flexWrap: 'wrap', gap: 6 }}>
                  {dayLabels.map((label, index) => (
                    <label key={label} className="chip" style={{ cursor: 'pointer', gap: 6 }}>
                      <input
                        type="checkbox"
                        checked={days.includes(index)}
                        onChange={() => setDays((current) => toggle(current, index))}
                      />
                      {label}
                    </label>
                  ))}
                </div>
              </div>

              <div className="row" style={{ gap: 10, marginTop: 18 }}>
                <div className="field" style={{ marginTop: 0, flex: 1 }}>
                  <label htmlFor="window-start">เริ่ม (UTC)</label>
                  <input id="window-start" className="input" type="time" value={windowStart} onChange={(e) => setWindowStart(e.target.value)} />
                </div>
                <div className="field" style={{ marginTop: 0, flex: 1 }}>
                  <label htmlFor="window-end">สิ้นสุด (00:00 = เที่ยงคืน)</label>
                  <input id="window-end" className="input" type="time" value={windowEnd} onChange={(e) => setWindowEnd(e.target.value)} />
                </div>
              </div>

              <div className="row" style={{ gap: 10, marginTop: 18 }}>
                <div className="field" style={{ marginTop: 0, flex: 1 }}>
                  <label htmlFor="active-from">เริ่มใช้</label>
                  <input id="active-from" className="input" type="date" value={activeFrom} onChange={(e) => setActiveFrom(e.target.value)} />
                </div>
                <div className="field" style={{ marginTop: 0, flex: 1 }}>
                  <label htmlFor="active-until">สิ้นสุดการใช้</label>
                  <input id="active-until" className="input" type="date" value={activeUntil} onChange={(e) => setActiveUntil(e.target.value)} />
                </div>
              </div>

              <div className="field">
                <label htmlFor="policy-priority">ลำดับความสำคัญ (0-1000)</label>
                <input
                  id="policy-priority"
                  className="input"
                  type="number"
                  min={0}
                  max={1000}
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                />
              </div>

              <label className="row" style={{ gap: 8, marginTop: 14, cursor: 'pointer', fontSize: 12.5 }}>
                <input type="checkbox" checked={enabled} onChange={() => setEnabled((current) => !current)} />
                เปิดใช้นโยบายนี้ทันที
              </label>

              <div className="row" style={{ marginTop: 18 }}>
                <button className="btn btn-primary" onClick={() => create.mutate()} disabled={!canSubmit}>
                  {create.isPending ? 'กำลังบันทึก…' : 'สร้างนโยบาย'}
                </button>
              </div>

              {create.error ? (
                <div className="muted" style={{ marginTop: 12, fontSize: 12.5, color: 'var(--color-amber)' }}>
                  {create.error instanceof Error ? create.error.message : 'สร้างนโยบายไม่สำเร็จ'}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </>
  )
}
